import { useEffect, useMemo } from 'react';
import useTableFiltering from 'shared/components/table/hooks/table-filtering';

import useFaultsHistoryFilters from 'features/faults-history/context/faults-history-filter-hook';
import ReadFaultsResponse from 'features/faults-history/domain/models/read-faults-response';
import { useReadFaultsHistory } from 'features/faults-history/domain/queries/faults-history-queries';
import Fault from '../domain/models/fault';

export interface FaultsHistoryHook {
  readFaultsHistoryData: ReadFaultsResponse | undefined;
  readFaultsHistoryIsLoading: boolean;
  currentPage: number;
  currentRowsPerPage: number;
  handleOnRowsPerPageChanged: (rowsPerPage: number) => void;
  handleOnPageChanged: (page: number) => void;
  refresh: () => void;
}

const useFaultsHistoryHook = (): FaultsHistoryHook => {
  const { filters } = useFaultsHistoryFilters();

  const {
    resetPaging,
    currentPagination: [currentPagination],
    currentPage,
    currentRowsPerPage,
    handleOnPageChanged,
    handleOnRowsPerPageChanged,
  } = useTableFiltering<Fault>();

  const query = useMemo(
    () => ({
      pagination: currentPagination,
      searchQuery: filters.searchBarValue,
      filters: filters.activeFilters,
      dateFrom: filters.dateFilters.dateFrom,
      dateTo: filters.dateFilters.dateTo,
    }),
    [currentPagination, filters.searchBarValue, filters.activeFilters, filters.dateFilters.dateFrom, filters.dateFilters.dateTo],
  );

  const {
    refetch: triggerReadFaultsHistory,
    data: readFaultsHistoryData,
    isPending: readFaultsHistoryIsLoading,
  } = useReadFaultsHistory(query);

  useEffect(() => {
    resetPaging();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const refresh = () => {
    triggerReadFaultsHistory();
  };

  return {
    readFaultsHistoryData,
    currentPage,
    readFaultsHistoryIsLoading,
    currentRowsPerPage,
    handleOnRowsPerPageChanged,
    handleOnPageChanged,
    refresh,
  };
};

export default useFaultsHistoryHook;
